// This config should only be imported by the top-level file
// that runs React.render to ensure fast refresh works.
import { createAnimations } from '@tamagui/animations-moti';
import { Easing } from 'react-native-reanimated';
import { createMedia, createTamagui, createTokens } from 'tamagui';

import {
    colorPrimitiveTokens,
    type ColorTokens,
    colorTokens,
} from './themes/SmoothieColorTokens';
import { fontTokens } from './themes/SmoothieFontTokens';
import {
    iconSizeTokens,
    radiusTokens,
    sizeTokens,
    spaceTokens,
} from './themes/SmoothieSizingTokens';
import { themes } from './themes/SmoothieThemes';

const animations = createAnimations({
    // Use these for basic, simple animations
    slow: {
        type: 'timing',
        duration: 500,
        easing: Easing.bezier(0.65, 0, 0.35, 1),
    },
    quick: {
        type: 'timing',
        duration: 200,
        easing: Easing.bezier(0.65, 0, 0.35, 1),
    },
    quicker: {
        type: 'timing',
        duration: 100,
        easing: Easing.bezier(0.65, 0, 0.35, 1),
    },

    // Use these for more lively animations
    bouncy: {
        type: 'spring',
        damping: 9,
        mass: 0.9,
        stiffness: 150,
    },
    lazy: {
        type: 'spring',
        damping: 18,
        stiffness: 50,
    },
    superLazy: {
        type: 'spring',
        damping: 18,
        stiffness: 20,
    },

    // Use these for animating specific components
    button: {
        type: 'timing',
        duration: 200,
        easing: Easing.bezier(0.65, 0, 0.35, 1),
    },
    tooltip: {
        type: 'spring',
        damping: 10,
        mass: 0.9,
        stiffness: 100,
    },
    sheet: {
        type: 'timing',
        duration: 600,
        easing: Easing.bezier(0.65, 0, 0.35, 1),
    },
    slideScreen: {
        type: 'timing',
        duration: 400,
        easing: Easing.bezier(0.65, 0, 0.35, 1),
    },
});

const media = createMedia({
    mobile_mini: { maxWidth: 385 }, // Small iPhones ~375px (Not very common)
    mobile_norm: { minWidth: 386 }, // Standard iPhones ~390px (Very common)
    mobile_plus: { minWidth: 420 }, // Pro Max iPhones ~430px (Somewhat common)
    sm: { minWidth: 770 },
    md: { minWidth: 1100 },
    lg: { minWidth: 1440 },

    short: { maxHeight: 820 },
    tall: { minHeight: 820 },
    hoverNone: { hover: 'none' },
    pointerCoarse: { pointer: 'coarse' },
});

const zIndexTokens = {
    0: 0,
    1: 100,
    2: 200,
    3: 300,
    4: 400,
    5: 500,
};

const tokens = createTokens({
    color: colorPrimitiveTokens,
    radius: radiusTokens,
    size: sizeTokens,
    space: spaceTokens,
    zIndex: zIndexTokens,
    icon: iconSizeTokens,
});

export interface TokensLibrary {
    color: ColorTokens;
    radius: typeof radiusTokens;
    size: typeof sizeTokens;
    space: typeof spaceTokens;
    icon: typeof iconSizeTokens;
    zIndex: typeof zIndexTokens;
    font: typeof fontTokens;
}
// tamagui doesn't categorize the font defs as tokens, so this is
// added for convenience for consumers. Also, the colorTokens object
// is replaced with a more convenient format for consumption.
const allTokens: TokensLibrary = {
    color: colorTokens,
    radius: radiusTokens,
    size: sizeTokens,
    space: spaceTokens,
    zIndex: zIndexTokens,
    icon: iconSizeTokens,
    font: fontTokens,
};
export { allTokens as tokens };

const customConfig = createTamagui({
    animations,
    fonts: fontTokens,
    themes,
    tokens,
    media,
    settings: {
        defaultFont: 'body',
        disableSSR: true,
        themeClassNameOnRoot: true,
        maxDarkLightNesting: 1,
        shouldAddPrefersColorThemes: false, // Set this to true when we support dark mode (must be true if we are supporting system preference for light and dark mode themes)
    },
});

declare module 'tamagui' {
    // overrides TamaguiCustomConfig so that custom types
    // work everywhere `tamagui` is imported
    export interface TamaguiCustomConfig extends TamaguiConfig {}
}

declare module '@tamagui/toast' {
    interface CustomData {
        icon?: React.ReactNode;
        button?: React.ReactNode;
        description?: string;
    }
}

export type TamaguiConfig = typeof customConfig;
export const smoothieTamaguiConfig = customConfig;
